import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router";
import { AppLayout } from "@/components/layout/AppLayout";
import { LoginPage } from "@/components/auth/LoginPage";
import { ForgotPasswordPage } from "@/components/auth/ForgotPasswordPage";
import { AcceptInvitePage } from "@/components/auth/AcceptInvitePage";
import { AdminPage } from "@/components/admin/AdminPage";
import { RoadmapPage } from "@/components/roadmap/RoadmapPage";
import { CliAuthorizePage } from "@/components/cli/CliAuthorizePage";
import { DocsPage } from "@/components/docs/DocsPage";
import { ToastContainer } from "@/components/common/Toast";
import { CreditExhaustedBanner } from "@/components/common/CreditExhaustedBanner";
import { ImpersonationBanner } from "@/components/common/ImpersonationBanner";
import { TooltipLayer } from "@/components/common/TooltipLayer";
import { UnsavedChangesGuardDialog } from "@/components/common/UnsavedChangesGuardDialog";
import { NotFoundPage } from "@/components/common/NotFoundPage";
import { IpNotAllowedPage } from "@/components/common/IpNotAllowedPage";
import { canAccessAdmin } from "@/lib/admin-access";
import { apiFetch } from "@/lib/api-client";
import { mainViewRouteRedirect } from "@/lib/main-view-access";
import { loginPathWithNext } from "@/lib/login-redirect";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { useIpRestrictionStore } from "@/stores/useIpRestrictionStore";
import { MAIN_VIEW_PATHS, type MainView } from "@/stores/useLayoutStore";
import { useOrgUsersStore } from "@/stores/useOrgUsersStore";

type AuthState = "checking" | "authenticated" | "unauthenticated";

/**
 * AppLayout を描くルートは必ずこの単一のラッパー型を使うこと。ルート間でラッパーの
 * コンポーネント型が異なると、タブ切り替え (URL 遷移) のたびに React が AppLayout を
 * アンマウント→再マウントし、非表示のまま保持しているチャットツリー全体
 * (全メッセージの Markdown 描画など) を作り直してしまう。
 */
function ProtectedRoute({
	auth,
	element,
	view,
}: {
	auth: AuthState;
	element: React.ReactElement;
	/** 権限の要るメインビューのルートで指定する。 */
	view?: MainView;
}) {
	const role = useCurrentUserStore((s) => s.role);
	const permissions = useCurrentUserStore((s) => s.permissions);
	const billingManagedInCloud = useCurrentUserStore((s) => s.billingManagedInCloud);
	const deviceManagementAvailable = useCurrentUserStore((s) => s.deviceManagementAvailable);
	if (view) {
		const redirect = mainViewRouteRedirect(auth, view, {
			role,
			permissions,
			billingManagedInCloud,
			deviceManagementAvailable,
		});
		return redirect ? <Navigate to={redirect} replace /> : element;
	}
	return auth === "authenticated" ? element : <Navigate to="/login" replace />;
}

function GuestRoute({ auth, element }: { auth: AuthState; element: React.ReactElement }) {
	return auth === "authenticated" ? <Navigate to="/" replace /> : element;
}

/** 未ログインならログイン後にこのページへ戻す。 */
function ReturningProtectedRoute({
	auth,
	element,
}: {
	auth: AuthState;
	element: React.ReactElement;
}) {
	const location = useLocation();
	if (auth === "authenticated") return element;
	return <Navigate to={loginPathWithNext(location.pathname + location.search)} replace />;
}

function AdminRoute({ auth, element }: { auth: AuthState; element: React.ReactElement }) {
	const role = useCurrentUserStore((s) => s.role);
	if (auth !== "authenticated") return <Navigate to="/login" replace />;
	if (!canAccessAdmin(role)) return <Navigate to="/" replace />;
	return element;
}

function RoadmapRoute({ auth, element }: { auth: AuthState; element: React.ReactElement }) {
	const roadmapAvailable = useCurrentUserStore((s) => s.roadmapAvailable);
	if (auth !== "authenticated") return <Navigate to="/login" replace />;
	if (!roadmapAvailable) return <Navigate to="/" replace />;
	return element;
}

export default function App() {
	const [auth, setAuth] = useState<AuthState>("checking");
	const setUser = useCurrentUserStore((s) => s.setUser);
	const ipBlocked = useIpRestrictionStore((s) => s.blocked);

	useEffect(() => {
		apiFetch("/api/auth/me", { redirectOn401: false })
			.then(async (res) => {
				if (res.ok) {
					const data = await res.json();
					setUser({
						id: data.id ?? "",
						firstName: data.firstName ?? "",
						lastName: data.lastName ?? "",
						email: data.email ?? "",
						role: data.role ?? "Member",
						permissions: data.permissions ?? [],
						organizationId: data.organizationId ?? "",
						organizationName: data.organizationName ?? "",
						creditExhausted: data.creditExhausted ?? false,
						deviceManagementAvailable: data.deviceManagementAvailable ?? false,
						helpAgentAvailable: data.helpAgentAvailable ?? false,
						billingManagedInCloud: data.billingManagedInCloud ?? false,
						roadmapAvailable: data.roadmapAvailable ?? false,
						knowledgeGraphAvailable: data.knowledgeGraphAvailable ?? false,
						impersonator: data.impersonator ?? null,
					});
					// Kick off the org-user cache as soon as we know who's logged in.
					// Message bubbles rely on this to render non-self senders by name.
					useOrgUsersStore.getState().load();
					setAuth("authenticated");
				} else if (!useIpRestrictionStore.getState().blocked) {
					setAuth("unauthenticated");
				}
			})
			.catch(() => setAuth("unauthenticated"));
	}, [setUser]);

	// ログインしていない公開ページ (招待受諾など) は拒否をその場のエラーとして出す。
	if (ipBlocked && auth !== "unauthenticated") return <IpNotAllowedPage />;
	if (auth === "checking") return null;

	return (
		<BrowserRouter>
			<Routes>
				<Route path="/login" element={<GuestRoute auth={auth} element={<LoginPage />} />} />
				<Route path="/forgot-password" element={<ForgotPasswordPage />} />
				<Route path="/accept-invite/:inviteId" element={<AcceptInvitePage />} />
				<Route
					path="/cli/authorize"
					element={<ReturningProtectedRoute auth={auth} element={<CliAuthorizePage />} />}
				/>
				{/* 配布済みのデバイスの登録画面が案内する旧 URL。 */}
				<Route path="/admin/devices" element={<Navigate to="/devices" replace />} />
				<Route path="/admin" element={<AdminRoute auth={auth} element={<AdminPage />} />} />
				<Route path="/admin/:tab" element={<AdminRoute auth={auth} element={<AdminPage />} />} />
				<Route
					path="/infra"
					element={<AdminRoute auth={auth} element={<AdminPage tab="infra" />} />}
				/>
				<Route
					path="/roadmap/:taskId?"
					element={<RoadmapRoute auth={auth} element={<RoadmapPage />} />}
				/>
				{/* /docs から先頭のページへ移っても、取得した利用ガイドを取り直さないよう 1 つのルートにする */}
				<Route
					path="/docs/:page?"
					element={<ReturningProtectedRoute auth={auth} element={<DocsPage />} />}
				/>
				<Route path="/chats/new" element={<ProtectedRoute auth={auth} element={<AppLayout />} />} />
				<Route
					path="/chats/:chatId"
					element={<ProtectedRoute auth={auth} element={<AppLayout />} />}
				/>
				<Route path="/projects" element={<ProtectedRoute auth={auth} element={<AppLayout />} />} />
				<Route path="/volumes" element={<ProtectedRoute auth={auth} element={<AppLayout />} />} />
				<Route
					path="/organization/:tab"
					element={<ProtectedRoute auth={auth} element={<AppLayout />} view="organization" />}
				/>
				{(["billing", "organization", "devices"] as const).map((view) => (
					<Route
						key={view}
						path={MAIN_VIEW_PATHS[view]}
						element={<ProtectedRoute auth={auth} element={<AppLayout />} view={view} />}
					/>
				))}
				<Route
					path="/projects/:projectId"
					element={<ProtectedRoute auth={auth} element={<AppLayout />} />}
				/>
				<Route path="/" element={<ProtectedRoute auth={auth} element={<AppLayout />} />} />
				<Route path="*" element={<NotFoundPage />} />
			</Routes>
			<ToastContainer />
			<TooltipLayer />
			<UnsavedChangesGuardDialog />
			<ImpersonationBanner />
			<CreditExhaustedBanner />
		</BrowserRouter>
	);
}
